export const COLORS = {
  // Background colors
  BACKGROUND: '#0a0a0a',
  UI_BACKGROUND: 0x111111,
  UI_BACKGROUND_HOVER: 0x222222,
  UI_BACKGROUND_PRESSED: 0x333333,

  // Player unit colors
  UNIT_TANK: 0x00ff00,
  UNIT_RANGED: 0x00ffff,
  UNIT_CHARGER: 0xff00ff,
  UNIT_DEFAULT: 0xffffff,

  // Enemy colors
  ENEMY_MELEE: 0xff0000,
  ENEMY_RANGED: 0xff6666,
  ENEMY_DEFAULT: 0xff0000,

  // UI colors
  TEXT_PRIMARY: '#ffffff',
  TEXT_SECONDARY: '#444444',
  TEXT_DISABLED: '#666666',
  TEXT_ACCENT: '#00ffff',
  TEXT_HIGHLIGHT: '#ff00ff',
  TEXT_DANGER: '#ff0000',

  // Health bar colors
  HEALTH_HIGH: 0x00ff00,
  HEALTH_MEDIUM: 0xffff00,
  HEALTH_LOW: 0xff0000,
  HEALTH_BACKGROUND: 0x333333,
  HEALTH_BORDER: 0x666666,

  // Base colors
  BASE_COLOR: 0x00ffff,
  BASE_BORDER: 0x00ffff,
  BASE_BACKGROUND: 0x333333,

  // Border colors
  BORDER_DEFAULT: 0x333333,
  BORDER_HOVER: 0x00ffff,
  BORDER_DISABLED: 0x444444,
} as const;

export const SIZES = {
  // Screen sizes
  SCREEN_WIDTH: 720,
  SCREEN_HEIGHT: 1280,

  // Unit sizes
  UNIT_TANK_WIDTH: 40,
  UNIT_TANK_HEIGHT: 40,
  UNIT_RANGED_RADIUS: 20,
  UNIT_CHARGER_SIZE: 40, // Triangle with points 20 units from center
  UNIT_DEFAULT_SIZE: 30,

  // Enemy sizes
  ENEMY_MELEE_WIDTH: 36,
  ENEMY_MELEE_HEIGHT: 36,
  ENEMY_RANGED_RADIUS: 16,
  ENEMY_DEFAULT_SIZE: 30,

  // UI Button sizes
  BUTTON_MIN_WIDTH: 140,
  BUTTON_MIN_HEIGHT: 80,
  BUTTON_MIN_FONT_SIZE: 24,

  // Charger storage UI
  CHARGER_STORAGE_MIN_RADIUS: 50,
  CHARGER_STORAGE_MIN_FONT_SIZE: 20,

  // Base dimensions
  BASE_WIDTH: 200,
  BASE_HEIGHT: 120,
  BASE_HEALTH_BAR_WIDTH: 200,
  BASE_HEALTH_BAR_HEIGHT: 16,
} as const;

export const POSITIONS = {
  // Vertical positioning
  BASE_LINE_OFFSET: 240,
  UNIT_SPAWN_Y_OFFSET: 200,
  ENEMY_SPAWN_Y: 60,

  // Horizontal positioning
  MIN_MARGIN: 100,
  UI_MARGIN: 40,
  CHARGER_STORAGE_OFFSET: 40,

  // Tactical positioning
  HALF_SCREEN_NO_GO_ZONE: 100,
  TANK_FRONTLINE_BUFFER: 200,
  RANGED_SAFE_DISTANCE: 160,
  CHARGER_BASE_DISTANCE: 100,

  // UI positioning
  BUTTON_BOTTOM_MARGIN: 20,
  TEXT_VERTICAL_OFFSET: 20,
  COUNTER_VERTICAL_OFFSET: 20,
} as const;

export const MOVEMENT = {
  // Speed multipliers
  FRAME_RATE: 0.016, // Roughly 60fps conversion
  CHARGER_SPEED_MULTIPLIER: 2,

  // Collision distances
  UNIT_COLLISION_RADIUS: 30,
  ENEMY_COLLISION_RADIUS: 30,
  CHARGER_COLLISION_DISTANCE: 40,

  // Push distances
  UNIT_PUSH_DISTANCE: 40,
  ENEMY_PUSH_DISTANCE: 30,

  // Movement speeds
  DEFAULT_UNIT_SPEED: 1.0, // Movement when no enemies
  RANGED_Y_SPEED_RATIO: 0.5, // Slower Y movement for ranged units
} as const;

export const TIMING = {
  // Spawn intervals
  UNIT_SPAWN_INTERVAL: 3000,
  ENEMY_BASE_SPAWN_INTERVAL: 2000,
  MIN_SPAWN_INTERVAL: 1000,
  MIN_ENEMY_SPAWN_INTERVAL: 500,

  // Difficulty scaling
  SPAWN_INTERVAL_REDUCTION: 0.9,
  SPAWN_WAVE_REDUCTION: 100,
  ENEMY_WAVE_REDUCTION: 100,

  // Animation timings
  BUTTON_HOVER_DURATION: 100,
  CHARGER_PULSE_DURATION: 500,
  CHARGER_STAGGER_DELAY: 100,
  CHARGER_RELEASE_DURATION: 1000,
  CHARGER_COOLDOWN_DURATION: 300,

  // Game timings
  DAMAGE_AMOUNT: 10,
  SCORE_PER_KILL: 10,
  SECONDS_PER_FRAME: 1000,
} as const;

export const UI_LAYOUT = {
  // Responsive scaling
  BUTTON_WIDTH_RATIO: 0.18,
  BUTTON_HEIGHT_RATIO: 0.1,
  BUTTON_FONT_RATIO: 0.03,

  CHARGER_RADIUS_RATIO: 0.06,
  CHARGER_FONT_RATIO: 0.025,

  // Label positioning
  LABEL_VERTICAL_RATIO: -0.15,
  COUNTER_VERTICAL_RATIO: 0.15,

  // UI element spacing
  BUTTON_SPACING: 5,
  UI_ELEMENT_SPACING: 20,
  ICON_SCALE_SMALL: 0.5,
  ICON_SCALE_MEDIUM: 0.6,
  ICON_SCALE_LARGE: 0.7,

  // Icon positions for charged state
  CHARGER_ICON_POSITIONS: [
    { x: -15, y: -10, scale: 0.6 },
    { x: 15, y: -10, scale: 0.6 },
    { x: -18, y: 5, scale: 0.5 },
    { x: 18, y: 5, scale: 0.5 },
    { x: 0, y: -20, scale: 0.7 }
  ],

  // Dot positions for charged state
  CHARGER_DOT_SPACING: 20,
  CHARGER_DOT_Y: 15,
} as const;

export const UNIT_STATS = {
  // Player Tank stats
  PLAYER_TANK: {
    HEALTH: 100,
    DAMAGE: 10,
    SPEED: 60,
    RANGE: 50, // Minimal range for melee engagement
    ATTACK_COOLDOWN: 1000, // 1 second
  },

  // Player Ranged stats
  PLAYER_RANGED: {
    HEALTH: 50,
    DAMAGE: 20,
    SPEED: 80,
    RANGE: 160, // Will be calculated dynamically (8 * 20 radius)
    ATTACK_COOLDOWN: 3000, // 3 seconds
  },

  // Player Charger stats
  PLAYER_CHARGER: {
    HEALTH: 70,
    DAMAGE: 20,
    SPEED: 160,
    RANGE: 60,
    MOVEMENT_DAMAGE_MULTIPLIER: 2, // Double damage when moving
    MOVEMENT_DAMAGE_REDUCTION: 0.5, // Half damage received when moving
  },

  // Enemy Melee (Tank) stats
  ENEMY_MELEE: {
    HEALTH: 50,
    DAMAGE: 10,
    SPEED: 80,
    RANGE: 50, // Minimal range for melee engagement
    ATTACK_COOLDOWN: 1000, // 1 second
  },

  // Enemy Ranged stats
  ENEMY_RANGED: {
    HEALTH: 30,
    DAMAGE: 10,
    SPEED: 60,
    RANGE: 128, // Will be calculated dynamically (8 * 16 radius)
    ATTACK_COOLDOWN: 2000, // 3 seconds
  },
} as const;

export const COMBAT_MECHANICS = {
  // Combat ranges
  TANK_CONTACT_RANGE: 50, // Distance for tank contact damage
  RANGED_RANGE_MULTIPLIER: 8, // Range multiplier for ranged units (increased for better ranged combat)

  // Attack timing
  TANK_ATTACK_COOLDOWN: 1000, // 1 second in milliseconds
  RANGED_ATTACK_COOLDOWN: 3000, // 3 seconds in milliseconds

  // Damage modifiers
  CHARGER_MOVING_DAMAGE_MULTIPLIER: 2.0,
  CHARGER_MOVING_DAMAGE_REDUCTION: 0.5,

  // Visual effects
  PROJECTILE_SPEED: 400, // Pixels per second
  PROJECTILE_LIFETIME: 2000, // Milliseconds

  // Combat detection
  COMBAT_UPDATE_INTERVAL: 100, // Check combat every 100ms
} as const;

export const GAME_BALANCE = {
  // Spawn patterns
  ENEMY_SPAWN_DELAY: 200,
  MAX_WAVE_DIFFICULTY_MULTIPLIER: 2.0,

  // Enemy type chances
  RANGED_CHANCE_BASE: 0.3,
  RANGED_CHANCE_MAX: 0.6,
  RANGED_CHANCE_INCREMENT: 0.05,

  // Damage multipliers
  DAMAGE_MULTIPLIER_BASE: 1.0,
  DAMAGE_MULTIPLIER_INCREMENT: 0.05,

  // Charger mechanics
  MAX_STORED_CHARGERS: 5,
  CHARGER_DAMAGE_MULTIPLIER: 2,

  // Health thresholds
  HEALTH_HIGH_THRESHOLD: 0.5,
  HEALTH_LOW_THRESHOLD: 0.25,
} as const;

export const VIEWPORT = {
  // Responsive breakpoints
  MOBILE_WIDTH: 320,
  MOBILE_HEIGHT: 568,
  TABLET_WIDTH: 768,
  TABLET_HEIGHT: 1024,

  // Scale limits
  MIN_SCALE_WIDTH: '100%',
  MIN_SCALE_HEIGHT: '100%',
} as const;
